import { useState } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Send, Users, AlertTriangle, MailCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { v: "all", label: "Alle Nutzer" },
  { v: "active", label: "Nur aktive Nutzer" },
  { v: "with_trades", label: "Nutzer mit ≥ 1 Trade" },
  { v: "with_strategies", label: "Nutzer mit ≥ 1 Strategie" },
  { v: "users", label: "Nur reguläre Nutzer (kein Admin)" },
  { v: "admins", label: "Nur Admins" },
];

export default function AdminBroadcast() {
  const [audience, setAudience] = useState("active");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [preview, setPreview] = useState(null);

  const previewMut = useMutation({
    mutationFn: async () => (await axios.post(`${API}/admin/broadcast`, {
      audience, subject: subject || "Vorschau", body_html: bodyHtml || "Vorschau", dry_run: true,
    }, { withCredentials: true })).data,
    onSuccess: (d) => setPreview(d),
    onError: (e) => toast.error("Vorschau fehlgeschlagen", { description: e?.response?.data?.detail }),
  });

  const sendMut = useMutation({
    mutationFn: async () => (await axios.post(`${API}/admin/broadcast`, {
      audience, subject, body_html: bodyHtml, dry_run: false,
    }, { withCredentials: true })).data,
    onSuccess: (d) => {
      toast.success("Broadcast versendet", { description: `${d.sent}/${d.count} zugestellt (${d.errors} Fehler)` });
      setSubject(""); setBodyHtml(""); setPreview(null);
    },
    onError: (e) => toast.error("Versand fehlgeschlagen", { description: e?.response?.data?.detail }),
  });

  const testMut = useMutation({
    mutationFn: async () => (await axios.post(`${API}/admin/broadcast/test`, {
      subject, body_html: bodyHtml,
    }, { withCredentials: true })).data,
    onSuccess: (d) => toast.success("Test-Mail versendet", { description: `An dich (${d.sent_to}) — andere Nutzer erhalten sie nicht.` }),
    onError: (e) => toast.error("Test-Versand fehlgeschlagen", { description: e?.response?.data?.detail }),
  });

  const canSend = subject.trim().length >= 3 && bodyHtml.trim().length >= 10;

  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Admin" title="Broadcast-Nachricht" description="Sende eine E-Mail an ausgewählte Nutzergruppen. Wird als HTML-Mail zugestellt, Absender ist TradeMind." testid="admin-broadcast-header" />

      <div className="card-surface p-5 space-y-4">
        <div>
          <Label className="overline">Zielgruppe</Label>
          <Select value={audience} onValueChange={(v) => { setAudience(v); setPreview(null); }}>
            <SelectTrigger className="mt-1.5" data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
            <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Betreff</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Kurz und klar" className="mt-1.5" data-testid="broadcast-subject" />
        </div>
        <div>
          <Label className="overline">Inhalt (HTML erlaubt)</Label>
          <Textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={8} placeholder="<p>Hallo,</p><p>Neu im Steuer-Tool: …</p>" className="mt-1.5 font-mono text-sm" data-testid="broadcast-body" />
          <p className="text-[11px] text-muted-foreground mt-1">Vor „Hi {"{name}"}" wird automatisch eingefügt, Footer ebenfalls.</p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button variant="outline" onClick={() => testMut.mutate()} disabled={!canSend || testMut.isPending} className="rounded-full" data-testid="broadcast-test-btn">
            {testMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailCheck className="h-4 w-4 mr-2" strokeWidth={1.5} />}
            Test-Versand an mich
          </Button>
          <Button variant="outline" onClick={() => previewMut.mutate()} disabled={previewMut.isPending} className="rounded-full" data-testid="broadcast-preview-btn">
            {previewMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" strokeWidth={1.5} />}
            Vorschau (Empfänger zählen)
          </Button>
          <Button onClick={() => { if (!preview) return toast.error("Bitte zuerst Vorschau ausführen."); if (!window.confirm(`Wirklich an ${preview.count} Empfänger senden?`)) return; sendMut.mutate(); }} disabled={!canSend || sendMut.isPending || !preview} className="rounded-full" data-testid="broadcast-send-btn">
            {sendMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" strokeWidth={1.5} />}
            An {preview?.count ?? "?"} Nutzer senden
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-border bg-secondary/40 p-4" data-testid="broadcast-preview-result">
            <div className="text-sm font-medium flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-primary" strokeWidth={1.5} /> {preview.count} Empfänger in Zielgruppe „{AUDIENCES.find(a=>a.v===preview.audience)?.label}"
            </div>
            {preview.count > 0 && (
              <div className="text-xs text-muted-foreground">
                Beispiel: {(preview.sample || []).join(", ")}{preview.count > 10 ? " …" : ""}
              </div>
            )}
            {preview.count === 0 && (
              <div className="text-xs text-[hsl(43_74%_66%)] flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3" /> Diese Zielgruppe enthält aktuell keine Nutzer.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
